import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';

// ============================================
// Types
// ============================================

interface FormData {
  name: string;
  email: string;
  company: string;
  role: string;
  companySize: string;
  challenge: string;
  timeline: string;
  selectedDate: Date | null;
  selectedTime: string;
}

interface FieldValidation {
  isValid: boolean;
  isTouched: boolean;
  error: string;
}

type ValidationState = Record<string, FieldValidation>;

// ============================================
// Data
// ============================================

const callAgenda = [
  {
    time: '5 min',
    description: "We'll ask about your biggest operational bottlenecks — where you're losing time, money, or sanity.",
    icon: '🎯',
  },
  {
    time: '10 min',
    description: "We'll show you specifically how AI can address them — with examples from similar companies.",
    icon: '💡',
  },
  {
    time: '10 min',
    description: "We'll map out a realistic timeline and scope a proposal tailored to your business.",
    icon: '📋',
  },
  {
    time: '5 min',
    description: "You'll leave with at least 3 actionable AI opportunities — even if we never work together.",
    icon: '🚀',
  },
];

const objections = [
  {
    objection: "\"I'm not sure if we're ready for AI.\"",
    response: "That's exactly what this call is for. 80% of our clients felt the same way before the first conversation. Readiness isn't a prerequisite — it's what we help you figure out.",
  },
  {
    objection: "\"We don't have the budget right now.\"",
    response: "Most of our solutions pay for themselves within 90 days. Let's find out if that math works for your situation. If it doesn't, we'll tell you.",
  },
  {
    objection: "\"I need to talk to my team first.\"",
    response: "Bring them on the call. Seriously. The more perspectives, the better the diagnosis. We've done calls with 8 people on the client side.",
  },
  {
    objection: "\"I've been burned by AI vendors before.\"",
    response: "So have most of our clients. That's actually why they chose us. Ask us about our 90-day ROI guarantee — it exists because we've seen too many companies get burned by empty promises.",
  },
];

const timelineOptions = [
  { value: 'asap', label: 'ASAP — we needed this yesterday' },
  { value: '30days', label: 'Within 30 days' },
  { value: 'exploring', label: 'Just exploring options' },
];

const timeSlots = [
  { time: '9:00 AM', available: true },
  { time: '10:00 AM', available: true },
  { time: '11:00 AM', available: false },
  { time: '1:00 PM', available: true },
  { time: '2:00 PM', available: true },
  { time: '3:00 PM', available: false },
  { time: '4:00 PM', available: true },
];

// ============================================
// Floating Label Input Component
// ============================================

interface FloatingLabelInputProps {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  validation: FieldValidation;
  required?: boolean;
}

const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onBlur,
  validation,
  required = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const isFloating = isFocused || value.length > 0;

  return (
    <motion.div
      className="relative"
      animate={
        validation.isTouched && !validation.isValid
          ? { x: [0, -10, 10, -10, 10, 0] }
          : {}
      }
      transition={{ duration: 0.5 }}
    >
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          onBlur();
        }}
        required={required}
        className={`
          w-full px-4 pt-6 pb-2 bg-zinc-800/50 border rounded-xl text-white
          transition-all duration-300 peer
          ${validation.isTouched && !validation.isValid
            ? 'border-red-500/50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
            : validation.isTouched && validation.isValid
            ? 'border-emerald-500/50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
            : 'border-zinc-700/50 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20'
          }
          focus:outline-none
        `}
        placeholder=" "
      />
      <motion.label
        htmlFor={name}
        className={`
          absolute left-4 pointer-events-none transition-all duration-300
          ${isFloating
            ? 'top-2 text-xs'
            : 'top-1/2 -translate-y-1/2 text-sm'
          }
          ${validation.isTouched && !validation.isValid
            ? 'text-red-400'
            : validation.isTouched && validation.isValid
            ? 'text-emerald-400'
            : isFocused
            ? 'text-blue-400'
            : 'text-zinc-500'
          }
        `}
        initial={false}
      >
        {label}
      </motion.label>

      {/* Validation icon */}
      <AnimatePresence>
        {validation.isTouched && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className="absolute right-4 top-1/2 -translate-y-1/2"
          >
            {validation.isValid ? (
              <motion.svg
                className="w-5 h-5 text-emerald-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.3 }}
              >
                <motion.path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.svg>
            ) : (
              <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error message */}
      <AnimatePresence>
        {validation.isTouched && !validation.isValid && validation.error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-red-400 text-xs mt-1 ml-1"
          >
            {validation.error}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ============================================
// Floating Label Textarea Component
// ============================================

interface FloatingLabelTextareaProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur: () => void;
  validation: FieldValidation;
  rows?: number;
}

const FloatingLabelTextarea: React.FC<FloatingLabelTextareaProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  validation,
  rows = 4,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const isFloating = isFocused || value.length > 0;

  return (
    <motion.div
      className="relative"
      animate={
        validation.isTouched && !validation.isValid
          ? { x: [0, -10, 10, -10, 10, 0] }
          : {}
      }
      transition={{ duration: 0.5 }}
    >
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          onBlur();
        }}
        rows={rows}
        className={`
          w-full px-4 pt-6 pb-2 bg-zinc-800/50 border rounded-xl text-white resize-none
          transition-all duration-300 peer
          ${validation.isTouched && !validation.isValid
            ? 'border-red-500/50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
            : validation.isTouched && validation.isValid
            ? 'border-emerald-500/50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
            : 'border-zinc-700/50 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20'
          }
          focus:outline-none
        `}
        placeholder=" "
      />
      <motion.label
        htmlFor={name}
        className={`
          absolute left-4 pointer-events-none transition-all duration-300
          ${isFloating
            ? 'top-2 text-xs'
            : 'top-4 text-sm'
          }
          ${validation.isTouched && !validation.isValid
            ? 'text-red-400'
            : validation.isTouched && validation.isValid
            ? 'text-emerald-400'
            : isFocused
            ? 'text-blue-400'
            : 'text-zinc-500'
          }
        `}
        initial={false}
      >
        {label}
      </motion.label>
    </motion.div>
  );
};

// ============================================
// Floating Label Select Component
// ============================================

interface FloatingLabelSelectProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur: () => void;
  validation: FieldValidation;
  options: { value: string; label: string }[];
}

const FloatingLabelSelect: React.FC<FloatingLabelSelectProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  validation,
  options,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const isFloating = isFocused || value.length > 0;

  return (
    <motion.div
      className="relative"
      animate={
        validation.isTouched && !validation.isValid
          ? { x: [0, -10, 10, -10, 10, 0] }
          : {}
      }
      transition={{ duration: 0.5 }}
    >
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          onBlur();
        }}
        className={`
          w-full px-4 pt-6 pb-2 bg-zinc-800/50 border rounded-xl text-white appearance-none cursor-pointer
          transition-all duration-300 peer
          ${validation.isTouched && !validation.isValid
            ? 'border-red-500/50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
            : validation.isTouched && validation.isValid
            ? 'border-emerald-500/50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
            : 'border-zinc-700/50 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20'
          }
          focus:outline-none
        `}
      >
        <option value="" disabled></option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <motion.label
        htmlFor={name}
        className={`
          absolute left-4 pointer-events-none transition-all duration-300
          ${isFloating
            ? 'top-2 text-xs'
            : 'top-1/2 -translate-y-1/2 text-sm'
          }
          ${validation.isTouched && !validation.isValid
            ? 'text-red-400'
            : validation.isTouched && validation.isValid
            ? 'text-emerald-400'
            : isFocused
            ? 'text-blue-400'
            : 'text-zinc-500'
          }
        `}
        initial={false}
      >
        {label}
      </motion.label>

      {/* Dropdown arrow */}
      <svg
        className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </motion.div>
  );
};

// ============================================
// Form Progress Indicator
// ============================================

interface FormProgressProps {
  currentStep: number;
  totalSteps: number;
  stepLabels: string[];
}

const FormProgress: React.FC<FormProgressProps> = ({ currentStep, totalSteps, stepLabels }) => {
  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm text-zinc-400">
          Step {currentStep} of {totalSteps}
        </span>
        <span className="text-sm text-blue-400 font-medium">
          {stepLabels[currentStep - 1]}
        </span>
      </div>

      <div className="relative h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-violet-500 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </div>

      {/* Step indicators */}
      <div className="flex justify-between mt-3">
        {stepLabels.map((label, index) => {
          const stepNum = index + 1;
          const isCompleted = stepNum < currentStep;
          const isCurrent = stepNum === currentStep;

          return (
            <div key={index} className="flex flex-col items-center">
              <motion.div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium
                  transition-all duration-300
                  ${isCompleted
                    ? 'bg-gradient-to-r from-blue-500 to-violet-500 text-white'
                    : isCurrent
                    ? 'bg-blue-500/20 border-2 border-blue-500 text-blue-400'
                    : 'bg-zinc-800 border border-zinc-700 text-zinc-500'
                  }
                `}
                animate={isCurrent ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {isCompleted ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  stepNum
                )}
              </motion.div>
              <span className={`
                text-xs mt-2 hidden sm:block
                ${isCurrent ? 'text-blue-400' : isCompleted ? 'text-zinc-400' : 'text-zinc-600'}
              `}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// Calendar Date Picker Component
// ============================================

interface CalendarPickerProps {
  selectedDate: Date | null;
  onSelectDate: (date: Date) => void;
}

const CalendarPicker: React.FC<CalendarPickerProps> = ({ selectedDate, onSelectDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const daysInMonth = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();

    const days: (Date | null)[] = [];

    // Add padding for days before the first day
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  }, [currentMonth]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || date.getDay() === 0 || date.getDay() === 6;
  };

  const isDateSelected = (date: Date) => {
    if (!selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + (direction === 'next' ? 1 : -1));
      return newDate;
    });
  };

  return (
    <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-4">
      {/* Calendar header */}
      <div className="flex items-center justify-between mb-4">
        <motion.button
          type="button"
          onClick={() => navigateMonth('prev')}
          className="p-2 rounded-lg hover:bg-zinc-700/50 text-zinc-400 hover:text-white transition-colors"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>
        <motion.span
          key={currentMonth.toString()}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-white font-medium"
        >
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </motion.span>
        <motion.button
          type="button"
          onClick={() => navigateMonth('next')}
          className="p-2 rounded-lg hover:bg-zinc-700/50 text-zinc-400 hover:text-white transition-colors"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </motion.button>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map((day) => (
          <div key={day} className="text-center text-xs text-zinc-500 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {daysInMonth.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="p-2" />;
          }

          const disabled = isDateDisabled(date);
          const selected = isDateSelected(date);

          return (
            <motion.button
              key={date.toISOString()}
              type="button"
              onClick={() => !disabled && onSelectDate(date)}
              disabled={disabled}
              className={`
                relative p-2 rounded-lg text-sm transition-all
                ${disabled
                  ? 'text-zinc-600 cursor-not-allowed'
                  : selected
                  ? 'bg-gradient-to-r from-blue-500 to-violet-500 text-white font-medium'
                  : 'text-zinc-300 hover:bg-zinc-700/50'
                }
              `}
              whileHover={!disabled ? { scale: 1.1 } : {}}
              whileTap={!disabled ? { scale: 0.95 } : {}}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.01 }}
            >
              {date.getDate()}
              {selected && (
                <motion.div
                  className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/20 to-violet-500/20"
                  layoutId="selectedDate"
                  transition={{ type: 'spring', bounce: 0.3 }}
                />
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-4 text-xs text-zinc-500">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-zinc-600"></span>
          Unavailable
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-500"></span>
          Selected
        </span>
      </div>
    </div>
  );
};

// ============================================
// Time Slot Selector Component
// ============================================

interface TimeSlotSelectorProps {
  selectedTime: string;
  onSelectTime: (time: string) => void;
  selectedDate: Date | null;
}

const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  selectedTime,
  onSelectTime,
  selectedDate,
}) => {
  if (!selectedDate) {
    return (
      <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-6 text-center">
        <svg className="w-12 h-12 mx-auto text-zinc-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-zinc-500 text-sm">Please select a date first</p>
      </div>
    );
  }

  return (
    <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-4">
      <p className="text-sm text-zinc-400 mb-4">
        Available times for{' '}
        <span className="text-white font-medium">
          {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </span>
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {timeSlots.map((slot, index) => {
          const isSelected = selectedTime === slot.time;

          return (
            <motion.button
              key={slot.time}
              type="button"
              onClick={() => slot.available && onSelectTime(slot.time)}
              disabled={!slot.available}
              className={`
                relative p-3 rounded-lg text-sm font-medium transition-all
                ${!slot.available
                  ? 'bg-zinc-800/30 text-zinc-600 cursor-not-allowed'
                  : isSelected
                  ? 'bg-gradient-to-r from-blue-500 to-violet-500 text-white'
                  : 'bg-zinc-700/30 text-zinc-300 hover:bg-zinc-700/50'
                }
              `}
              whileHover={slot.available ? { scale: 1.02 } : {}}
              whileTap={slot.available ? { scale: 0.98 } : {}}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <span className="flex items-center justify-center gap-2">
                {slot.time}
                {!slot.available && (
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                )}
                {slot.available && !isSelected && (
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                )}
              </span>
            </motion.button>
          );
        })}
      </div>

      <p className="text-xs text-zinc-500 mt-4 text-center">
        All times are in your local timezone (EST)
      </p>
    </div>
  );
};

// ============================================
// Success Animation Component
// ============================================

interface SuccessAnimationProps {
  email: string;
  selectedDate: Date | null;
  selectedTime: string;
  onReset: () => void;
}

const SuccessAnimation: React.FC<SuccessAnimationProps> = ({
  email,
  selectedDate,
  selectedTime,
  onReset,
}) => {
  const checkmarkPath = useMotionValue(0);
  const checkmarkOpacity = useTransform(checkmarkPath, [0, 1], [0, 1]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      checkmarkPath.set(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [checkmarkPath]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="text-center py-8"
    >
      {/* Animated checkmark */}
      <motion.div
        className="relative w-24 h-24 mx-auto mb-8"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', bounce: 0.5, delay: 0.2 }}
      >
        <motion.div
          className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-500/20 to-emerald-500/10"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.2, 0.5],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <div className="absolute inset-2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 flex items-center justify-center">
          <svg
            className="w-12 h-12 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
          >
            <motion.path
              d="M5 13l4 4L19 7"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              style={{ opacity: checkmarkOpacity }}
            />
          </svg>
        </div>
      </motion.div>

      <motion.h3
        className="text-2xl font-bold text-white mb-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        You're All Set!
      </motion.h3>

      <motion.p
        className="text-zinc-400 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        Your strategy session has been scheduled
      </motion.p>

      {/* Confirmation email preview */}
      <motion.div
        className="bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-6 text-left mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-zinc-700/50">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <p className="text-white font-medium text-sm">Confirmation email sent</p>
            <p className="text-zinc-500 text-xs">Check your inbox at {email}</p>
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div>
              <p className="text-zinc-400">Date</p>
              <p className="text-white font-medium">
                {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-zinc-400">Time</p>
              <p className="text-white font-medium">{selectedTime} (EST)</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <div>
              <p className="text-zinc-400">Meeting</p>
              <p className="text-white font-medium">30-min video call (link in email)</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Calendar buttons */}
      <motion.div
        className="flex flex-wrap justify-center gap-3 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
      >
        {['Google Calendar', 'Outlook', 'Apple Calendar'].map((cal) => (
          <button
            key={cal}
            className="px-4 py-2 text-sm bg-zinc-800/50 border border-zinc-700/50 rounded-lg text-zinc-400 hover:text-white hover:border-zinc-600 transition-colors"
          >
            Add to {cal}
          </button>
        ))}
      </motion.div>

      <motion.button
        onClick={onReset}
        className="text-blue-400 hover:text-blue-300 text-sm font-medium"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        Schedule another call
      </motion.button>
    </motion.div>
  );
};

// ============================================
// Live Chat Fallback Component
// ============================================

const LiveChatFallback: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: 'spring' }}
      >
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="relative w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 text-white shadow-lg shadow-blue-500/25 flex items-center justify-center"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.svg
                key="close"
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </motion.svg>
            ) : (
              <motion.svg
                key="chat"
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </motion.svg>
            )}
          </AnimatePresence>

          {/* Pulse indicator */}
          <motion.span
            className="absolute top-0 right-0 w-3 h-3 bg-emerald-400 rounded-full"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [1, 0.7, 1],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.button>
      </motion.div>

      {/* Chat popup */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed bottom-24 right-6 w-80 bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl z-50 overflow-hidden"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
          >
            <div className="p-4 bg-gradient-to-r from-blue-500/20 to-violet-500/20 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 flex items-center justify-center text-white text-sm font-bold">
                    AI
                  </div>
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-400 rounded-full border-2 border-zinc-900"></span>
                </div>
                <div>
                  <p className="text-white font-medium text-sm">AI Deploy Support</p>
                  <p className="text-emerald-400 text-xs">Online now</p>
                </div>
              </div>
            </div>

            <div className="p-4">
              <div className="bg-zinc-800/50 rounded-lg p-3 mb-4">
                <p className="text-zinc-300 text-sm">
                  Hi there! Need help scheduling your strategy session? I'm here to help.
                </p>
              </div>

              <div className="space-y-2">
                <button className="w-full p-3 text-left text-sm bg-zinc-800/30 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors">
                  I have a question about the audit
                </button>
                <button className="w-full p-3 text-left text-sm bg-zinc-800/30 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors">
                  I need help with scheduling
                </button>
                <button className="w-full p-3 text-left text-sm bg-zinc-800/30 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors">
                  Talk to a human
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============================================
// Main Contact Page Component
// ============================================

export const ContactPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    role: '',
    companySize: '',
    challenge: '',
    timeline: '',
    selectedDate: null,
    selectedTime: '',
  });

  const [validation, setValidation] = useState<ValidationState>({
    name: { isValid: false, isTouched: false, error: '' },
    email: { isValid: false, isTouched: false, error: '' },
    company: { isValid: false, isTouched: false, error: '' },
    role: { isValid: false, isTouched: false, error: '' },
    companySize: { isValid: false, isTouched: false, error: '' },
    challenge: { isValid: false, isTouched: false, error: '' },
    timeline: { isValid: false, isTouched: false, error: '' },
  });

  const validateField = (name: string, value: string): { isValid: boolean; error: string } => {
    switch (name) {
      case 'name':
        if (!value.trim()) return { isValid: false, error: 'Name is required' };
        if (value.trim().length < 2) return { isValid: false, error: 'Name must be at least 2 characters' };
        return { isValid: true, error: '' };

      case 'email':
        if (!value.trim()) return { isValid: false, error: 'Email is required' };
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return { isValid: false, error: 'Please enter a valid email' };
        return { isValid: true, error: '' };

      case 'company':
        if (!value.trim()) return { isValid: false, error: 'Company name is required' };
        return { isValid: true, error: '' };

      case 'role':
        if (!value.trim()) return { isValid: false, error: 'Your role is required' };
        return { isValid: true, error: '' };

      case 'companySize':
        if (!value) return { isValid: false, error: 'Please select company size' };
        return { isValid: true, error: '' };

      case 'challenge':
        return { isValid: true, error: '' }; // Optional field

      case 'timeline':
        if (!value) return { isValid: false, error: 'Please select a timeline' };
        return { isValid: true, error: '' };

      default:
        return { isValid: true, error: '' };
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Real-time validation if field has been touched
    if (validation[name]?.isTouched) {
      const { isValid, error } = validateField(name, value);
      setValidation(prev => ({
        ...prev,
        [name]: { ...prev[name], isValid, error },
      }));
    }
  };

  const handleBlur = (name: string) => {
    const value = formData[name as keyof typeof formData] as string;
    const { isValid, error } = validateField(name, value);
    setValidation(prev => ({
      ...prev,
      [name]: { isValid, isTouched: true, error },
    }));
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return (
          validateField('name', formData.name).isValid &&
          validateField('email', formData.email).isValid &&
          validateField('company', formData.company).isValid &&
          validateField('role', formData.role).isValid
        );
      case 2:
        return (
          validateField('companySize', formData.companySize).isValid &&
          validateField('timeline', formData.timeline).isValid
        );
      case 3:
        return formData.selectedDate !== null && formData.selectedTime !== '';
      default:
        return false;
    }
  };

  const handleNextStep = () => {
    // Validate current step fields
    if (currentStep === 1) {
      ['name', 'email', 'company', 'role'].forEach((field) => {
        const value = formData[field as keyof typeof formData] as string;
        const { isValid, error } = validateField(field, value);
        setValidation(prev => ({
          ...prev,
          [field]: { isValid, isTouched: true, error },
        }));
      });
    } else if (currentStep === 2) {
      ['companySize', 'timeline'].forEach((field) => {
        const value = formData[field as keyof typeof formData] as string;
        const { isValid, error } = validateField(field, value);
        setValidation(prev => ({
          ...prev,
          [field]: { isValid, isTouched: true, error },
        }));
      });
    }

    if (isStepValid(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isStepValid(3)) return;

    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));

    console.log('Form submitted:', formData);
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const handleReset = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      role: '',
      companySize: '',
      challenge: '',
      timeline: '',
      selectedDate: null,
      selectedTime: '',
    });
    setValidation({
      name: { isValid: false, isTouched: false, error: '' },
      email: { isValid: false, isTouched: false, error: '' },
      company: { isValid: false, isTouched: false, error: '' },
      role: { isValid: false, isTouched: false, error: '' },
      companySize: { isValid: false, isTouched: false, error: '' },
      challenge: { isValid: false, isTouched: false, error: '' },
      timeline: { isValid: false, isTouched: false, error: '' },
    });
    setCurrentStep(1);
    setIsSubmitted(false);
  };

  const stepLabels = ['Contact Info', 'Business Details', 'Schedule'];

  return (
    <main className="bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        {/* Animated background blobs */}
        <motion.div
          className="absolute top-1/4 left-1/3 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[150px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-violet-500/10 rounded-full blur-[120px]"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 md:py-32">
          <div className="max-w-3xl">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
            >
              <motion.span
                className="w-1.5 h-1.5 rounded-full bg-blue-400"
                animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="text-zinc-300 text-sm font-medium">Book a Call</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-[1.1]"
            >
              Your AI Transformation Starts With a
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400"> 30-Minute Conversation</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="text-xl text-zinc-400 leading-relaxed"
            >
              This is not a sales pitch. It is a diagnostic. You will leave smarter about AI opportunities in your business — regardless of whether we work together.
            </motion.p>
          </div>
        </div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
      </section>

      {/* Main Content */}
      <section className="relative py-16 sm:py-20 md:py-24 overflow-hidden">
        {/* Top border */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Left Column - Info */}
            <div className="space-y-16">
              {/* What Happens on the Call */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
                >
                  <motion.span
                    className="w-1.5 h-1.5 rounded-full bg-blue-400"
                    animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <span className="text-zinc-300 text-sm font-medium">The Agenda</span>
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                  className="text-2xl md:text-3xl font-bold text-white mb-8"
                >
                  What Happens on the Call
                </motion.h2>

                <div className="space-y-4">
                  {callAgenda.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
                      whileHover={{ x: 5 }}
                      className="flex gap-4 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50 backdrop-blur-sm hover:border-zinc-700/50 transition-colors"
                    >
                      <div className="flex-shrink-0 flex flex-col items-center gap-2">
                        <motion.div
                          className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-lg"
                          whileHover={{ scale: 1.1 }}
                        >
                          {item.icon}
                        </motion.div>
                        <span className="text-blue-400 text-xs font-semibold">{item.time}</span>
                      </div>
                      <p className="text-zinc-300 leading-relaxed pt-2">
                        {item.description}
                      </p>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                  className="mt-6 relative"
                >
                  <div className="absolute -inset-[1px] bg-gradient-to-r from-emerald-500/30 to-emerald-500/10 rounded-xl" />
                  <div className="relative p-5 rounded-xl bg-zinc-900/90 backdrop-blur-sm">
                    <p className="text-emerald-400 font-medium flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Total time: 30 minutes. Zero fluff. You'll know exactly where you stand.
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Objection Handling */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
                >
                  <motion.span
                    className="w-1.5 h-1.5 rounded-full bg-amber-400"
                    animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <span className="text-zinc-300 text-sm font-medium">Common Concerns</span>
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                  className="text-2xl md:text-3xl font-bold text-white mb-8"
                >
                  We Get It. Here's the Truth.
                </motion.h2>

                <div className="space-y-4">
                  {objections.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
                      whileHover={{ scale: 1.01 }}
                      className="p-5 rounded-xl bg-zinc-900/50 border border-zinc-800/50 backdrop-blur-sm hover:border-zinc-700/50 transition-all"
                    >
                      <p className="text-amber-400/80 italic mb-3 text-sm">{item.objection}</p>
                      <p className="text-zinc-300 leading-relaxed">{item.response}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="sticky top-32"
              >
                <div className="relative">
                  <motion.div
                    className="absolute -inset-[1px] bg-gradient-to-b from-blue-500/40 via-violet-500/20 to-transparent rounded-2xl"
                    animate={{
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />

                  <div className="relative bg-zinc-900/95 backdrop-blur-sm rounded-2xl p-8">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-2">
                      <motion.div
                        className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center"
                        animate={{ rotate: [0, 5, -5, 0] }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </motion.div>
                      <div>
                        <h3 className="text-xl font-bold text-white">
                          Book Your Free Strategy Session
                        </h3>
                        <div className="flex items-center gap-2">
                          <p className="text-zinc-500 text-sm">
                            We'll respond within 24 hours
                          </p>
                          {/* Estimated response time indicator */}
                          <motion.span
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-xs rounded-full"
                            animate={{ opacity: [0.7, 1, 0.7] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            <span className="w-1 h-1 bg-emerald-400 rounded-full"></span>
                            Avg: 4 hours
                          </motion.span>
                        </div>
                      </div>
                    </div>

                    <AnimatePresence mode="wait">
                      {isSubmitted ? (
                        <SuccessAnimation
                          email={formData.email}
                          selectedDate={formData.selectedDate}
                          selectedTime={formData.selectedTime}
                          onReset={handleReset}
                        />
                      ) : (
                        <motion.div
                          key="form"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          {/* Progress indicator */}
                          <div className="mt-6 mb-6">
                            <FormProgress
                              currentStep={currentStep}
                              totalSteps={3}
                              stepLabels={stepLabels}
                            />
                          </div>

                          <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
                            <AnimatePresence mode="wait">
                              {/* Step 1: Contact Info */}
                              {currentStep === 1 && (
                                <motion.div
                                  key="step1"
                                  initial={{ opacity: 0, x: 20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -20 }}
                                  className="space-y-4"
                                >
                                  <div className="grid grid-cols-2 gap-4">
                                    <FloatingLabelInput
                                      label="Your Name"
                                      name="name"
                                      value={formData.name}
                                      onChange={handleChange}
                                      onBlur={() => handleBlur('name')}
                                      validation={validation.name}
                                      required
                                    />
                                    <FloatingLabelInput
                                      label="Work Email"
                                      name="email"
                                      type="email"
                                      value={formData.email}
                                      onChange={handleChange}
                                      onBlur={() => handleBlur('email')}
                                      validation={validation.email}
                                      required
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <FloatingLabelInput
                                      label="Company"
                                      name="company"
                                      value={formData.company}
                                      onChange={handleChange}
                                      onBlur={() => handleBlur('company')}
                                      validation={validation.company}
                                      required
                                    />
                                    <FloatingLabelInput
                                      label="Your Role"
                                      name="role"
                                      value={formData.role}
                                      onChange={handleChange}
                                      onBlur={() => handleBlur('role')}
                                      validation={validation.role}
                                      required
                                    />
                                  </div>
                                </motion.div>
                              )}

                              {/* Step 2: Business Details */}
                              {currentStep === 2 && (
                                <motion.div
                                  key="step2"
                                  initial={{ opacity: 0, x: 20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -20 }}
                                  className="space-y-4"
                                >
                                  <FloatingLabelSelect
                                    label="Company Size"
                                    name="companySize"
                                    value={formData.companySize}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('companySize')}
                                    validation={validation.companySize}
                                    options={[
                                      { value: '10-50', label: '10-50 employees' },
                                      { value: '51-200', label: '51-200 employees' },
                                      { value: '201-500', label: '201-500 employees' },
                                      { value: '501-1000', label: '501-1,000 employees' },
                                      { value: '1000+', label: '1,000+ employees' },
                                    ]}
                                  />
                                  <FloatingLabelTextarea
                                    label="What's your biggest operational challenge?"
                                    name="challenge"
                                    value={formData.challenge}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('challenge')}
                                    validation={validation.challenge}
                                    rows={3}
                                  />
                                  <FloatingLabelSelect
                                    label="How soon do you want to start?"
                                    name="timeline"
                                    value={formData.timeline}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('timeline')}
                                    validation={validation.timeline}
                                    options={timelineOptions}
                                  />
                                </motion.div>
                              )}

                              {/* Step 3: Schedule */}
                              {currentStep === 3 && (
                                <motion.div
                                  key="step3"
                                  initial={{ opacity: 0, x: 20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -20 }}
                                  className="space-y-4"
                                >
                                  <CalendarPicker
                                    selectedDate={formData.selectedDate}
                                    onSelectDate={(date) => setFormData(prev => ({ ...prev, selectedDate: date }))}
                                  />
                                  <TimeSlotSelector
                                    selectedDate={formData.selectedDate}
                                    selectedTime={formData.selectedTime}
                                    onSelectTime={(time) => setFormData(prev => ({ ...prev, selectedTime: time }))}
                                  />
                                </motion.div>
                              )}
                            </AnimatePresence>

                            {/* Navigation buttons */}
                            <div className="flex gap-3 pt-4">
                              {currentStep > 1 && (
                                <motion.button
                                  type="button"
                                  onClick={handlePrevStep}
                                  className="flex-1 py-4 bg-zinc-800 text-white font-semibold rounded-xl hover:bg-zinc-700 transition-colors"
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  Back
                                </motion.button>
                              )}

                              {currentStep < 3 ? (
                                <motion.button
                                  type="button"
                                  onClick={handleNextStep}
                                  className="flex-1 py-4 bg-gradient-to-r from-blue-500 to-violet-500 text-white font-semibold rounded-xl relative overflow-hidden group"
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-blue-400 to-violet-400"
                                    initial={{ x: '-100%' }}
                                    whileHover={{ x: 0 }}
                                    transition={{ duration: 0.3 }}
                                  />
                                  <span className="relative flex items-center justify-center gap-2">
                                    Continue
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                  </span>
                                </motion.button>
                              ) : (
                                <motion.button
                                  type="submit"
                                  disabled={isSubmitting || !isStepValid(3)}
                                  className={`
                                    flex-1 py-4 font-semibold rounded-xl relative overflow-hidden
                                    ${isSubmitting || !isStepValid(3)
                                      ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
                                      : 'bg-gradient-to-r from-emerald-500 to-emerald-400 text-white'
                                    }
                                  `}
                                  whileHover={!isSubmitting && isStepValid(3) ? { scale: 1.02 } : {}}
                                  whileTap={!isSubmitting && isStepValid(3) ? { scale: 0.98 } : {}}
                                >
                                  <span className="relative flex items-center justify-center gap-2">
                                    {isSubmitting ? (
                                      <>
                                        <motion.div
                                          className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                                          animate={{ rotate: 360 }}
                                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                        />
                                        Scheduling...
                                      </>
                                    ) : (
                                      <>
                                        Confirm Booking
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                      </>
                                    )}
                                  </span>
                                </motion.button>
                              )}
                            </div>

                            {/* Social proof near submit */}
                            <motion.div
                              className="flex items-center justify-center gap-3 pt-2"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.5 }}
                            >
                              <div className="flex -space-x-2">
                                {[1, 2, 3, 4].map((i) => (
                                  <div
                                    key={i}
                                    className="w-6 h-6 rounded-full bg-gradient-to-br from-zinc-600 to-zinc-700 border-2 border-zinc-900"
                                  />
                                ))}
                              </div>
                              <p className="text-zinc-500 text-sm">
                                <span className="text-emerald-400 font-semibold">127 audits</span> completed this quarter
                              </p>
                            </motion.div>
                          </form>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Alternative CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="mt-6 p-5 rounded-xl bg-zinc-900/30 border border-zinc-800/30 text-center"
                >
                  <p className="text-zinc-400 text-sm mb-2">
                    Prefer email? Reach out to
                  </p>
                  <motion.a
                    href="mailto:hello@aideploy.com"
                    className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
                    whileHover={{ scale: 1.05 }}
                  >
                    hello@aideploy.com
                  </motion.a>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Urgency Footer */}
      <section className="relative py-12 sm:py-16 md:py-20 overflow-hidden">
        {/* Animated background */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-amber-500/5 rounded-full blur-[100px]"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Top border */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Enterprise trust badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
          >
            {[
              {
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                ),
                title: 'NDA Protected',
                subtitle: 'Full confidentiality',
              },
              {
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: '30-Min Strategy Call',
                subtitle: 'No obligation',
              },
              {
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ),
                title: 'Custom Roadmap',
                subtitle: 'Tailored to your needs',
              },
              {
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                ),
                title: 'ROI Guarantee',
                subtitle: '90-day results',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.1 + index * 0.1 }}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/30 border border-zinc-700/30"
              >
                <div className="w-10 h-10 rounded-lg bg-zinc-800 border border-zinc-700/50 flex items-center justify-center text-zinc-400">
                  {item.icon}
                </div>
                <div className="text-center">
                  <p className="text-white text-sm font-medium">{item.title}</p>
                  <p className="text-zinc-500 text-xs">{item.subtitle}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Availability notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-zinc-800/50 border border-zinc-700/30"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <p className="text-zinc-400 text-sm">
              <span className="text-zinc-300">2 slots available</span> for March 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Live Chat Fallback */}
      <LiveChatFallback />
    </main>
  );
};

export default ContactPage;
